import React, { lazy, Suspense, useState, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, createRoutesFromElements, Route, Outlet, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, AppBar, Toolbar } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { EditModeProvider, useEditMode } from './context/EditModeContext';

// Стили
import './App.css';

// Кастомный провайдер темы
import { ThemeProvider } from './utils/theme';
// Провайдер аутентификации
import { AuthProvider } from './context/AuthContext';
// Провайдер локализации
import { LocalizationProvider } from './utils/localization';
// Компонент для защищенных маршрутов
import ProtectedRoute from './components/admin/ProtectedRoute';

// Мобильная оптимизация и PWA поддержка
import { addDeviceClasses } from './utils/deviceDetection';
import { preloadCriticalResources } from './utils/preloadResources';
import NetworkStatus from './components/common/NetworkStatus';

// Layouts
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Ленивая загрузка страниц и компонентов
const Home = lazy(() => import('./components/pages/Home'));
const SkillsPage = lazy(() => import('./components/pages/SkillsPage'));
// Используем новую улучшенную версию страницы проектов
const ProjectsPage = lazy(() => import('./components/pages/ProjectsPage.jsx'));
const ExperiencePage = lazy(() => import('./components/pages/ExperiencePage'));
const ContactPage = lazy(() => import('./components/pages/ContactPage'));
// Используем новый улучшенный универсальный компонент детальной страницы проекта
const ProjectDetail = lazy(() => import('./components/pages/EnhancedProjectDetail.jsx'));
const OfflinePage = lazy(() => import('./offline'));

// Компоненты визуального редактора
const VisualEditor = lazy(() => import('./components/editor/VisualEditor'));
const EditorToolbar = lazy(() => import('./components/editor/EditorToolbar'));
const PropertiesPanel = lazy(() => import('./components/editor/PropertiesPanel'));
const ElementSelector = lazy(() => import('./components/editor/ElementSelector'));

// Компоненты админ-панели
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const LoginPage = lazy(() => import('./components/admin/LoginPage'));
const Dashboard = lazy(() => import('./components/admin/Dashboard'));
const SiteSettings = lazy(() => import('./components/admin/SiteSettings'));
const UiSettings = lazy(() => import('./components/admin/UiSettings'));
const DemoEditable = lazy(() => import('./components/admin/DemoEditable'));
const BackupsManager = lazy(() => import('./components/admin/BackupsManager'));

// Компонент загрузки для React.Suspense
const LoadingFallback = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      flexDirection: 'column',
      minHeight: '100vh',
      p: 3
    }}
  >
    <CircularProgress size={60} thickness={4} />
    <Typography variant="h6" sx={{ mt: 2 }}>
      Загрузка...
    </Typography>
  </Box>
);

// Компонент-обертка для маршрутов в режиме редактирования
const EditModeWrapper = () => {
  const { setEditMode, editMode } = useEditMode();
  const location = useLocation();
  
  // Включаем режим редактирования при монтировании компонента
  useEffect(() => {
    console.log("🔍 Активирован режим редактирования");
    setEditMode(true);
    
    return () => {
      console.log("❌ Режим редактирования отключен");
      setEditMode(false);
    };
  }, []); // Пустой массив зависимостей - только при монтировании
  
  // Загружаем редактор и его компоненты с отступом для панели инструментов
  return (
    <>
      {/* Панель редактирования и компоненты редактора */}
      <Suspense fallback={<Box sx={{ height: '60px' }} />}>
        <EditorToolbar />
      </Suspense>
      
      <Suspense fallback={<div />}>
        <PropertiesPanel />
      </Suspense>
      
      <Suspense fallback={<div />}>
        <ElementSelector />
      </Suspense>
      
      {/* Основной контент страницы с отступом для панели инструментов */}
      <Box sx={{ pt: '64px', pb: 0 }}>
        <Outlet />
      </Box>
    </>
  );
};

// Компонент для кнопки быстрого выхода из режима редактирования
const EditModeExitButton = () => {
  const { editMode } = useEditMode();
  const navigate = useNavigate();
  
  if (!editMode) return null;
  
  const handleExit = () => {
    const currentPath = window.location.pathname;
    const newPath = currentPath.replace('/edit', '') || '/';
    navigate(newPath);
  };
  
  return (
    <Button
      variant="contained"
      color="error"
      onClick={handleExit}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1300,
        boxShadow: 3
      }}
    >
      Выйти из режима
    </Button>
  );
};

// Обычный макет сайта
const MainLayout = () => {
  return (
    <div className="App">
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
      <EditModeExitButton />
    </div>
  );
};

// Создаем маршруты
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Корневой маршрут для перенаправления на Edit или View */}
      <Route path="/edit" element={<MainLayout />}>
        {/* Контейнер для режима редактирования с EditToolbar */}
        <Route element={<EditModeWrapper />}>
          <Route index element={<Suspense fallback={<LoadingFallback />}><Home /></Suspense>} />
          <Route path="skills" element={<Suspense fallback={<LoadingFallback />}><SkillsPage /></Suspense>} />
          <Route path="projects" element={<Suspense fallback={<LoadingFallback />}><ProjectsPage /></Suspense>} />
          <Route path="projects/:id" element={<Suspense fallback={<LoadingFallback />}><ProjectDetail /></Suspense>} />
          <Route path="experience" element={<Suspense fallback={<LoadingFallback />}><ExperiencePage /></Suspense>} />
          <Route path="contact" element={<Suspense fallback={<LoadingFallback />}><ContactPage /></Suspense>} />
        </Route>
      </Route>
      
      {/* Обычные маршруты сайта */}
      <Route path="/" element={<MainLayout />}>
        <Route index element={<Suspense fallback={<LoadingFallback />}><Home /></Suspense>} />
        <Route path="skills" element={<Suspense fallback={<LoadingFallback />}><SkillsPage /></Suspense>} />
        <Route path="projects" element={<Suspense fallback={<LoadingFallback />}><ProjectsPage /></Suspense>} />
        <Route path="projects/:id" element={<Suspense fallback={<LoadingFallback />}><ProjectDetail /></Suspense>} />
        <Route path="experience" element={<Suspense fallback={<LoadingFallback />}><ExperiencePage /></Suspense>} />
        <Route path="contact" element={<Suspense fallback={<LoadingFallback />}><ContactPage /></Suspense>} />
      </Route>
      
      {/* Маршруты админ-панели */}
      <Route path="/admin">
        {/* Страница входа */}
        <Route path="login" element={<Suspense fallback={<LoadingFallback />}><LoginPage /></Suspense>} />
        
        {/* Защищенные маршруты админ-панели */}
        <Route path="" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AdminLayout />
            </Suspense>
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="site-settings" element={<SiteSettings />} />
          <Route path="ui-settings" element={<UiSettings />} />
          <Route path="backups" element={<BackupsManager />} />
          <Route path="projects" element={<div>Управление проектами</div>} />
          <Route path="experience" element={<div>Управление опытом</div>} />
          <Route path="skills" element={<div>Управление навыками</div>} />
          <Route path="visual-editor" element={<DemoEditable />} />
          <Route index element={<Dashboard />} />
        </Route>
      </Route>
    </>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);

function App() {
  useEffect(() => {
    addDeviceClasses();
    preloadCriticalResources();
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <LocalizationProvider>
          <AuthProvider>
            <EditModeProvider>
              <NetworkStatus />
              <RouterProvider router={router} />
            </EditModeProvider>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
